import type { Pickup, UpdatePickupPayload } from "../../../../api/pickups";
import type { ClientPickupFormData } from "../../utils/types";
import type { PickupLocation } from "../../../../api/pickupLocations";

export function clientPickupFormDataToUpdatePayload(
  formData: ClientPickupFormData,
  options?: { isPickupLocationLocked?: boolean },
): UpdatePickupPayload {
  const {
    selectedPickupLocation,
    selectedDay,
    friendFirstName,
    friendLastName,
    friendPhone,
  } = formData;

  return {
    ...(!options?.isPickupLocationLocked &&
      selectedPickupLocation?._id != null && {
        pickupLocationId: selectedPickupLocation._id,
      }),
    ...(selectedDay !== "" && { pickupDay: selectedDay }),
    friendPickupFirstName: friendFirstName,
    friendPickupLastName: friendLastName,
    friendPickupPhone: friendPhone,
  };
}

export const checkIfThereAreChanges = (
  currentClientPickup: Pickup | null,
  clientPickupFormData: ClientPickupFormData,
  isPickupLocationLocked?: boolean,
) => {
  const {
    friendPickupChecked,
    friendFirstName,
    friendLastName,
    friendPhone,
    selectedDay,
    selectedPickupLocation,
  } = clientPickupFormData;

  const areThereChangesInFriendPickupFirstName =
    friendFirstName !== undefined &&
    currentClientPickup?.friendPickupFirstName !== friendFirstName;

  const areThereChangesInFriendPickupLastName =
    friendLastName !== undefined &&
    currentClientPickup?.friendPickupLastName !== friendLastName;

  const areThereChangesInFriendPickupPhone =
    friendPhone !== undefined &&
    currentClientPickup?.friendPickupPhone !== friendPhone;
  const wasThereACheckboxChange =
    (!friendPickupChecked && currentClientPickup?.friendPickupFirstName) ||
    (!friendPickupChecked && currentClientPickup?.friendPickupLastName) ||
    (!friendPickupChecked && currentClientPickup?.friendPickupPhone) ||
    (friendPickupChecked &&
      currentClientPickup?.friendPickupFirstName === null &&
      currentClientPickup?.friendPickupLastName === null &&
      currentClientPickup?.friendPickupPhone === null);
  const areThereChanges =
    currentClientPickup?.pickupDay !== selectedDay ||
    (!isPickupLocationLocked &&
      currentClientPickup?.pickupLocationId !== selectedPickupLocation?._id) ||
    areThereChangesInFriendPickupFirstName ||
    areThereChangesInFriendPickupLastName ||
    areThereChangesInFriendPickupPhone ||
    wasThereACheckboxChange;
  return areThereChanges;
};

export const isFriendPickupInfoComplete = (currentClientPickup: Pickup) => {
  if (
    currentClientPickup.friendPickupFirstName &&
    currentClientPickup.friendPickupFirstName !== "" &&
    currentClientPickup.friendPickupLastName &&
    currentClientPickup.friendPickupLastName !== "" &&
    currentClientPickup.friendPickupPhone &&
    currentClientPickup.friendPickupPhone !== ""
  ) {
    return true;
  }

  return false;
};

export const pickupInfoPages = [
  {
    title: "Important information",
    text: [
      "If you signed up for pickup at Alnarp's Farm, your pickup location stays at the farm for the season (you can still change the day if more than one is available).",
      "You can only change pickup location until 00:00 on your currently selected pickup day.",
      "If you want your pickup location to be the same every week, you don't need to change anything ever.",
      "If you change your pickup location, it stays at the new location for your next pickup. If you want to change it back to your 'default' location, you need to manually change it again.",
      "If you want a friend to pick up your harvest, this information needs to be added before 00:00 on your currently selected pickup day.",
    ],
  },
  {
    title: "How to change your Pickup location",
    text: [
      "Click the 'Change My Details' button below the map.",
      "Now you will see two drop-down menus, one for location and one for day.",
      "Start by choosing your preferred location. If there are several days available for that location, you'll see them in the next menu.",
      "Don't forget to save your changes!",
    ],
  },
  {
    title: "What if I can't pick up my harvest?",
    text: [
      "If you can't pick up your harvest, you'll need to find someone else who can pick it up for you.",
      "Click the 'Change My Details' button below the map.",
      "Start by checking the box called 'Someone else will pick up my delivery'.",
      "Fill in the contact information of your friend.",
      "Don't forget to save your changes!",
    ],
  },
];

export const shouldButtonBeDisabled = (
  pickupLocations: PickupLocation[],
  currentClientPickup: Pickup | null,
) => {
  const days = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];

  const currentPickupLocation = pickupLocations.find(
    (location) => location._id === currentClientPickup?.pickupLocationId,
  );
  const currentDayOption = currentPickupLocation?.days.find(
    (day) => day.day === currentClientPickup?.pickupDay,
  );
  const pickupStartTime = currentDayOption?.startTime;

  if (pickupStartTime) {
    const currentDay = days[new Date().getDay()];
    const [pickupStartHour, pickupStartMinutes] = pickupStartTime
      .split(":")
      .map(Number);

    if (currentClientPickup?.pickupDay === currentDay) {
      const currentDate = new Date();
      const currentPickupStartDate = new Date();

      currentPickupStartDate.setHours(pickupStartHour);
      currentPickupStartDate.setMinutes(pickupStartMinutes);
      currentPickupStartDate.setSeconds(0);

      const currentTime = currentDate.getTime(); // milliseconds from January 1st 1970 until now
      const pickupStartTime = currentPickupStartDate.getTime(); // milliseconds from January 1st 1970 until pickup start time

      if (currentTime < pickupStartTime) {
        return true;
      }
    }
  }

  return false;
};
